import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ChannelService } from 'src/app/services/channel.service';
import { FacebookService } from 'src/app/services/facebook.service';
import { InstagramService, InstagramUser } from 'src/app/services/instagram.service';
import { SystemConfigService } from 'src/app/services/system-config.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

export const SERVICE_LINE_GUIDE_IMAGE_SOURCES: ReadonlyArray<readonly string[]> = [
  ['line-developers-login.png', 'line-provider-create-full.png'],
  ['line-provider-channels-full.png', 'line-provider-id-full.png'],
  ['line-login-channel-id-full.png', 'line-login-channel-secret-full.png'],
  ['messaging-api-channel-id-full.png', 'messaging-api-channel-secret-full.png'],
  ['general-tenant-line-login.png'],
  ['service-number-line-oa.png'],
  ['messaging-api-webhook-full.png']
];

interface ThirdPartyServerBindingItem {
  id: string;
  bindingId: string;
  name: string;
  webhookUrl: string;
  eventTypes: ThirdPartyEventType[];
}

type ThirdPartyEventType = 'Follow' | 'Unfollow' | 'Messaging' | 'Postback';

type ServiceChannelBindingStatus = 'Available' | 'Binding' | 'Failed' | 'Initializing' | string;
type ServiceLineChannelType = 'Login' | 'Messaging' | string;

interface ServiceSocialChannelModel {
  accessToken?: string;
  avatarUrl?: string;
  bindErrorCode?: string;
  bindErrorMessage?: string;
  code?: string;
  createTime?: number;
  id?: string;
  memberId?: string;
  name?: string;
  pictureUrl?: string;
  serviceNumberId?: string;
  status?: ServiceChannelBindingStatus;
  tenantId?: string;
  updateTime?: number;
}

interface ServiceFacebookChannelModel extends ServiceSocialChannelModel {}

interface ServiceInstagramChannelModel extends ServiceSocialChannelModel {
  groupCode?: string;
  groupName?: string;
  ig_botName?: string;
  ig_name?: string;
}

interface ServiceLineChannelModel {
  bindErrorCode?: string;
  bindErrorMessage?: string;
  botBasicId?: string;
  businessCardLiffId?: string;
  channelId?: string;
  channelSecret?: string;
  channelType?: ServiceLineChannelType;
  code?: string;
  createTime?: number;
  gwBindingDeleted?: boolean;
  id?: string;
  joinAileLiffId?: string;
  liffId?: string;
  memberId?: string;
  messageRemainCount?: number;
  messageTotalCount?: number;
  messageUsedCount?: number;
  allowLineGroupSend?: boolean | null;
  name?: string;
  pictureUrl?: string;
  providerId?: string;
  serviceNumberId?: string;
  status?: ServiceChannelBindingStatus;
  tenantId?: string;
  updateTime?: number;
}

interface AdminServiceChannelVO {
  facebookModel?: ServiceFacebookChannelModel | null;
  instagramModel?: ServiceInstagramChannelModel | null;
  lineLoginModel?: ServiceLineChannelModel | null;
  lineModel?: ServiceLineChannelModel | null;
  serviceNumberId?: string;
}

@Component({
  selector: 'servicenumber-channel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzDividerModule,
    NzEmptyModule,
    NzGridModule,
    NzIconModule,
    NzModalModule,
    NzPopconfirmModule,
    NzSelectModule,
    NzSpinModule,
    NzSwitchModule,
    NzTabsModule,
    NzTagModule,
    NzToolTipModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './servicenumber-channel.component.html',
  styleUrls: ['./servicenumber-channel.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceChannelComponent implements OnInit, OnDestroy {
  readonly lineGuideImageSources = SERVICE_LINE_GUIDE_IMAGE_SOURCES;
  @Input() serviceNumberId: string = '';

  // 渠道数据
  lineChannel: ServiceLineChannelModel | null = null;
  lineLoginInfo: ServiceLineChannelModel | null = null;
  facebookChannel: ServiceFacebookChannelModel | null = null;
  instagramChannel: ServiceInstagramChannelModel | null = null;

  // 表单
  lineForm!: FormGroup;
  facebookForm!: FormGroup;
  instagramForm!: FormGroup;

  // 状态控制
  loading = false;
  lineBindLoading: boolean = false;
  facebookBindLoading: boolean = false;
  instagramBindLoading: boolean = false;
  lineModalVisible = false;
  lineGuideVisible = false;
  thirdPartyBindingLoading = false;
  thirdPartyBindingSubmitting = false;
  thirdPartyBindingModalVisible = false;
  thirdPartyEventModalVisible = false;
  thirdPartyBindings: ThirdPartyServerBindingItem[] = [];
  bindableThirdPartyServers: ThirdPartyServerBindingItem[] = [];
  bindableThirdPartyPageIndex = 0;
  bindableThirdPartyPageSize = 20;
  bindableThirdPartyHasNextPage = true;
  selectedThirdPartyServerId: string | null = null;
  selectedThirdPartyEventTypes: ThirdPartyEventType[] = [];
  editingThirdPartyBinding: ThirdPartyServerBindingItem | null = null;

  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly channelService = inject(ChannelService);
  private readonly facebookService = inject(FacebookService);
  private readonly instagramService = inject(InstagramService);
  private readonly systemConfigService = inject(SystemConfigService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  lineUnbindLoading: boolean = false;
  lineGroupSendUpdating = false;
  facebookUnbindLoading: boolean = false;
  instagramUnbindLoading: boolean = false;

  showLineSecret: boolean = false;
  showFacebookToken: boolean = false;
  showInstagramToken: boolean = false;
  readonly thirdPartyEventTypeLabels: Record<ThirdPartyEventType, string> = {
    Follow: 'tenant.third-party-service.events.follow',
    Unfollow: 'tenant.third-party-service.events.unfollow',
    Messaging: 'tenant.third-party-service.events.message',
    Postback: 'tenant.third-party-service.events.postback'
  };
  readonly thirdPartyEventTypeOptions: ThirdPartyEventType[] = ['Follow', 'Unfollow', 'Messaging', 'Postback'];
  ngOnInit(): void {
    this.initForms();
    if (this.serviceNumberId) {
      this.loadChannelData();
      this.loadThirdPartyBindings();
    }
  }

  ngOnDestroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  // Facebook授权登录
  loginWithFacebook(): void {
    const serviceNumberId = this.serviceNumberId;
    if (!serviceNumberId) {
      this.message.error(this.i18n.fanyi('servicenumber.channel.service-number-id-missing'));
      return;
    }

    // 设置本地存储参数
    localStorage.setItem('fb_binding_service_number_id', serviceNumberId);
    localStorage.setItem('fb_binding_service_channel', 'Facebook');

    const baseScope = ['pages_show_list', 'pages_read_engagement', 'pages_manage_metadata', 'pages_read_user_content', 'pages_messaging'];
    const pageScope = ['instagram_basic', 'instagram_manage_messages', 'business_management'];
    const scope = [...baseScope, ...pageScope];
    const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    this.systemConfigService.getFacebookAppId().subscribe(clientId => {
      const oauthUrl = this.facebookService.generateOAuthUrl(clientId, `${window.location.origin}/checkFBLogin`, scope, state);
      this.setupMessageListener();
      this.facebookService.openLoginPopup(oauthUrl);
    });
  }

  // Instagram授权登录
  loginWithInstagram(): void {
    const serviceNumberId = this.serviceNumberId;
    if (!serviceNumberId) {
      this.message.error(this.i18n.fanyi('servicenumber.channel.service-number-id-missing'));
      return;
    }

    // 设置本地存储参数
    localStorage.setItem('ig_binding_service_number_id', serviceNumberId);
    localStorage.setItem('ig_binding_service_channel', 'Instagram');

    const scope = [
      'instagram_business_basic',
      'instagram_business_manage_messages',
      'instagram_business_manage_comments',
      'instagram_business_content_publish'
    ];
    const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    this.instagramService.generateInstagramOAuthUrl(`${window.location.origin}/checkIGLogin`, scope, state).subscribe(oauthUrl => {
      this.setupMessageListener();
      this.facebookService.openLoginPopup(oauthUrl);
    });
  }

  // 设置消息监听器
  private setupMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data;

      if (data.type === 'BIND_SUCCESS') {
        this.message.success(this.i18n.fanyi('servicenumber.channel.auth-success'));
        this.loadChannelData(); // 重新加载渠道配置
        this.facebookBindLoading = false;
        this.instagramBindLoading = false;
      } else if (data.type === 'BIND_ERROR') {
        // Facebook/Instagram 串接失败
        this.message.error(data.message || this.i18n.fanyi('servicenumber.channel.page-bind-failed'));
        this.facebookBindLoading = false;
        this.instagramBindLoading = false;
      } else if (data.type === 'PAGE_SELECTED') {
        this.facebookBindLoading = true;
        this.instagramBindLoading = true;
        const channelType = data.channelType;
        const loadingMessage = this.message.loading(this.i18n.fanyi('channel.connecting-channel').replace('{0}', channelType), {
          nzDuration: 0
        });
        this.bindPages(data.pageData, channelType, loadingMessage);
      } else if (data.type === 'BIND_CANCELLED') {
        // 用户取消串接
        this.facebookBindLoading = false;
        this.instagramBindLoading = false;
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  private messageListener?: (event: MessageEvent) => void;

  // 绑定Facebook/Instagram页面
  private bindPages(pageData: any, channelType: string, loadingMessage: any): void {
    // 根据渠道类型构建不同的数据结构
    const bindData: any = {
      serviceNumberId: this.serviceNumberId,
      channelType: channelType
    };

    // 根据渠道类型设置不同的 DTO 参数名称
    if (channelType === 'Instagram') {
      bindData.instagramDto = {
        code: pageData.id,
        accessToken: pageData.accessToken,
        serviceNumberId: this.serviceNumberId,
        pictureUrl: pageData.pictureUrl
      };
    } else {
      bindData.facebookDto = {
        code: pageData.id,
        accessToken: pageData.access_token,
        serviceNumberId: this.serviceNumberId,
        pictureUrl: pageData.picture?.data?.url
      };
    }

    // 直接在这里处理绑定逻辑，确保加载消息能被正确关闭
    this.channelService
      .bindChannel(bindData)
      .pipe(
        finalize(() => {
          this.facebookBindLoading = false;
          this.instagramBindLoading = false;
          this.message.remove(loadingMessage.messageId);
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('servicenumber.channel.page-connect-success'));
            setTimeout(() => {
              this.loadChannelData();
            }, 1000);
          } else {
            this.message.error(res.msg || this.i18n.fanyi('servicenumber.channel.page-connect-failed'));
          }
        },
        error: error => {
          console.error('Channel bind error:', error);
          this.message.error(this.i18n.fanyi('servicenumber.channel.page-bind-failed'));
        }
      });
  }

  // 初始化表单
  initForms(): void {
    // Line表单
    this.lineForm = this.fb.group({
      channelId: [null, [Validators.required]],
      channelSecret: [null, [Validators.required]],
      providerId: [null, [Validators.required]]
    });

    // Facebook表单
    this.facebookForm = this.fb.group({
      code: [null, [Validators.required]],
      accessToken: [null, [Validators.required]]
    });

    // Instagram表单
    this.instagramForm = this.fb.group({
      code: [null, [Validators.required]],
      groupCode: [null, [Validators.required]],
      accessToken: [null, [Validators.required]]
    });
  }

  // 加载渠道数据
  loadChannelData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_SERVICENUMBER_CHANNEL_ITEM}/${this.serviceNumberId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          const channelData: AdminServiceChannelVO = res.data || {};

          // 设置各渠道数据
          this.lineChannel = channelData.lineModel || null;
          this.lineLoginInfo = channelData.lineLoginModel || null;
          this.facebookChannel = channelData.facebookModel || null;
          this.instagramChannel = channelData.instagramModel || null;

          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.channel.load-data-failed'));
        }
      });
  }

  loadThirdPartyBindings(): void {
    this.thirdPartyBindingLoading = true;
    this.http
      .post(this.buildServiceNumberExtensionListPath(this.serviceNumberId), {})
      .pipe(
        finalize(() => {
          this.thirdPartyBindingLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.thirdPartyBindings = this.normalizeThirdPartyBindings(res.data);
            return;
          }
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('servicenumber.channel.third-party.load-failed'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('servicenumber.channel.third-party.load-failed'));
        }
      });
  }

  // Line渠道相关方法
  showLineBindModal(): void {
    if (this.isLineBindDisabled()) {
      this.message.warning(this.getLineBindDisabledMessage());
      return;
    }

    this.lineForm.reset();
    this.lineModalVisible = true;
  }

  openLineGuide(): void {
    this.lineGuideVisible = true;
  }

  closeLineGuide(): void {
    this.lineGuideVisible = false;
  }

  isLineBindDisabled(): boolean {
    return !this.lineLoginInfo || this.lineLoginInfo.status === 'Failed';
  }

  isChannelAvailable(channel: { status?: ServiceChannelBindingStatus } | null): boolean {
    if (!channel) {
      return false;
    }
    if (!channel.status) {
      return true;
    }
    return channel.status === 'Available';
  }

  getChannelStatusText(channel: { status?: ServiceChannelBindingStatus } | null): string {
    const status = channel?.status;
    if (!status || status === 'Available') {
      return '';
    }
    if (status === 'Failed') {
      return this.i18n.fanyi('tenant.channel.personal.line-login.status.Failed');
    }
    const statusText = this.i18n.fanyi(`tenant.channel.personal.line-login.status.${status}`);
    return statusText === `tenant.channel.personal.line-login.status.${status}` ? status : statusText;
  }

  getChannelStatusClass(channel: { status?: ServiceChannelBindingStatus } | null): string {
    return channel?.status === 'Failed' ? 'card-status--danger' : '';
  }

  getChannelFailureReason(channel: { status?: ServiceChannelBindingStatus; bindErrorMessage?: string } | null): string {
    if (channel?.status !== 'Failed') {
      return '';
    }
    const rebindKey = 'tenant.channel.personal.line-login.rebind-hint';
    const translatedRebindHint = this.i18n.fanyi(rebindKey);
    const rebindHint = translatedRebindHint === rebindKey ? '请重新绑定。' : translatedRebindHint;
    const reason = channel.bindErrorMessage;
    if (!reason) {
      return rebindHint;
    }
    return /[。.!?！？]$/.test(reason) ? `${reason}${rebindHint}` : `${reason}。${rebindHint}`;
  }

  getLineBindDisabledMessage(): string {
    if (!this.lineLoginInfo) {
      return this.getTranslatedMessage('servicenumber.channel.line-login-required', '请先到租户配置串接 LINE Login');
    }

    return this.getLineLoginRebindRequiredMessage();
  }

  private getLineLoginRebindRequiredMessage(): string {
    return this.getTranslatedMessage(
      'servicenumber.channel.line-login-rebind-required',
      'LINE Login 绑定失败，请到租户配置重新串接 LINE Login'
    );
  }

  private getTranslatedMessage(key: string, fallback: string): string {
    const message = this.i18n.fanyi(key);
    return message && message !== key ? message : fallback;
  }

  handleLineCancel(): void {
    this.lineModalVisible = false;
  }

  bindLine(): void {
    if (this.lineForm.invalid) {
      Object.values(this.lineForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const data = {
      serviceNumberId: this.serviceNumberId,
      lineDto: {
        ...this.lineForm.value,
        serviceNumberId: this.serviceNumberId
      },
      channelType: 'Line'
    };
    this.lineBindLoading = true;
    this.channelService
      .bindChannel(data)
      .pipe(
        finalize(() => {
          this.lineBindLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        this.lineBindLoading = false;
        if (res.success) {
          this.message.success(this.i18n.fanyi('servicenumber.channel.line-bind-success'));
          this.lineModalVisible = false;
          this.loadChannelData();
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.channel.line-bind-failed'));
        }
      });
  }

  unbindLine(): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.channel.confirm-unbind'),
      nzContent: this.i18n.fanyi('servicenumber.channel.line-unbind-confirm'),
      nzOkText: this.i18n.fanyi('servicenumber.channel.confirm'),
      nzCancelText: this.i18n.fanyi('servicenumber.channel.cancel'),
      nzOnOk: () => {
        this.lineUnbindLoading = true;
        this.channelService
          .unbindChannel(this.serviceNumberId, 'Line')
          .pipe(
            finalize(() => {
              this.lineUnbindLoading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe(res => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('servicenumber.channel.line-unbind-success'));
              this.lineChannel = null;
              this.cdr.detectChanges();
            } else {
              this.message.error(this.i18n.fanyi('servicenumber.channel.line-unbind-failed'));
            }
          });
      }
    });
  }

  /** 切換 LINE 群組外發開關，開啟前要求確認計費風險。 */
  onLineGroupSendChange(eventOrChecked: MouseEvent | boolean): void {
    const previousValue = this.isLineGroupSendAllowed();
    const checked = typeof eventOrChecked === 'boolean' ? eventOrChecked : !previousValue;

    // nz-switch 會先更新自己的視覺狀態；確認或 API 完成前，模型必須維持已儲存的值。
    if (this.lineChannel) this.lineChannel.allowLineGroupSend = previousValue;
    this.cdr.detectChanges();

    if (checked && !previousValue) {
      this.modal.confirm({
        nzTitle: '確認開啟 LINE 群組主動推送',
        nzContent: 'LINE 群組主動推送按群成員總人數扣除 LINE 點數額度，極易產生高額帳單。',
        nzOkText: '我已明確了解計費風險',
        nzCancelText: this.i18n.fanyi('servicenumber.channel.cancel'),
        nzOnOk: () => this.saveLineGroupSendConfig(true),
        nzOnCancel: () => {
          if (this.lineChannel) this.lineChannel.allowLineGroupSend = previousValue;
          this.cdr.detectChanges();
        }
      });
      return;
    }
    this.saveLineGroupSendConfig(checked);
  }

  isLineGroupSendAllowed(): boolean {
    return this.lineChannel?.allowLineGroupSend === true;
  }

  private saveLineGroupSendConfig(allowLineGroupSend: boolean): void {
    this.lineGroupSendUpdating = true;
    this.channelService
      .updateLineGroupSendConfig(this.serviceNumberId, allowLineGroupSend)
      .pipe(
        finalize(() => {
          this.lineGroupSendUpdating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            if (this.lineChannel) this.lineChannel.allowLineGroupSend = allowLineGroupSend;
            this.message.success('LINE 群組外發設定已更新');
          } else {
            if (this.lineChannel) this.lineChannel.allowLineGroupSend = !allowLineGroupSend;
            this.message.error(res.msg || 'LINE 群組外發設定更新失敗');
          }
        },
        error: error => {
          if (this.lineChannel) this.lineChannel.allowLineGroupSend = !allowLineGroupSend;
          showFallbackHttpError(this.message, error?.error?.msg, 'LINE 群組外發設定更新失敗');
        }
      });
  }

  unbindFacebook(): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.channel.confirm-unbind'),
      nzContent: this.i18n.fanyi('servicenumber.channel.facebook-unbind-confirm'),
      nzOkText: this.i18n.fanyi('servicenumber.channel.confirm'),
      nzCancelText: this.i18n.fanyi('servicenumber.channel.cancel'),
      nzOnOk: () => {
        this.facebookUnbindLoading = true;
        this.channelService
          .unbindChannel(this.serviceNumberId, 'Facebook')
          .pipe(
            finalize(() => {
              this.facebookUnbindLoading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe(res => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('servicenumber.channel.facebook-unbind-success'));
              this.facebookChannel = null;
              this.cdr.detectChanges();
            } else {
              this.message.error(this.i18n.fanyi('servicenumber.channel.facebook-unbind-failed'));
            }
          });
      }
    });
  }

  unbindInstagram(): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.channel.confirm-unbind'),
      nzContent: this.i18n.fanyi('servicenumber.channel.instagram-unbind-confirm'),
      nzOkText: this.i18n.fanyi('servicenumber.channel.confirm'),
      nzCancelText: this.i18n.fanyi('servicenumber.channel.cancel'),
      nzOnOk: () => {
        this.instagramUnbindLoading = true;
        this.channelService
          .unbindChannel(this.serviceNumberId, 'Instagram')
          .pipe(
            finalize(() => {
              this.instagramUnbindLoading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe(res => {
            if (res.success) {
              this.message.success(this.i18n.fanyi('servicenumber.channel.instagram-unbind-success'));
              this.instagramChannel = null;
              this.cdr.detectChanges();
            } else {
              this.message.error(this.i18n.fanyi('servicenumber.channel.instagram-unbind-failed'));
            }
          });
      }
    });
  }

  // 辅助方法
  maskToken(token?: string | null): string {
    if (!token) return '';
    if (token.length <= 8) return '********';
    return `${token.substring(0, 4)}****${token.substring(token.length - 4)}`;
  }

  // 切换显示完整token的方法
  toggleLineSecret(): void {
    this.showLineSecret = !this.showLineSecret;
  }

  toggleFacebookToken(): void {
    this.showFacebookToken = !this.showFacebookToken;
  }

  toggleInstagramToken(): void {
    this.showInstagramToken = !this.showInstagramToken;
  }

  openThirdPartyBindingModal(): void {
    this.thirdPartyBindingModalVisible = true;
    this.thirdPartyEventModalVisible = false;
    this.editingThirdPartyBinding = null;
    this.selectedThirdPartyServerId = null;
    this.selectedThirdPartyEventTypes = [];
    this.loadBindableThirdPartyServers(true);
  }

  openThirdPartyBindingEditModal(item: ThirdPartyServerBindingItem): void {
    this.thirdPartyBindingModalVisible = false;
    this.thirdPartyEventModalVisible = true;
    this.editingThirdPartyBinding = item;
    this.selectedThirdPartyServerId = item.id;
    this.selectedThirdPartyEventTypes = item.eventTypes.length ? [...item.eventTypes] : [...this.thirdPartyEventTypeOptions];
    this.bindableThirdPartyServers = [];
  }

  closeThirdPartyBindingModal(): void {
    this.thirdPartyBindingModalVisible = false;
    this.bindableThirdPartyServers = [];
    this.bindableThirdPartyPageIndex = 0;
    this.bindableThirdPartyHasNextPage = true;
  }

  closeThirdPartyEventModal(): void {
    this.thirdPartyEventModalVisible = false;
    this.editingThirdPartyBinding = null;
    this.selectedThirdPartyServerId = null;
    this.selectedThirdPartyEventTypes = [];
  }

  loadBindableThirdPartyServers(reset: boolean = false): void {
    if (this.thirdPartyBindingLoading) {
      return;
    }
    if (!reset && !this.bindableThirdPartyHasNextPage) {
      return;
    }

    const pageIndex = reset ? 0 : this.bindableThirdPartyPageIndex;
    this.thirdPartyBindingLoading = true;
    this.http
      .post(ConstantUtil.ADMIN_EXTENSION_SERVER_V1_LIST, {
        pageIndex,
        pageSize: this.bindableThirdPartyPageSize,
        direction: 'desc',
        refreshTime: Date.now()
      })
      .pipe(
        finalize(() => {
          this.thirdPartyBindingLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            const boundServerIds = new Set(this.thirdPartyBindings.map(item => item.id));
            const normalizedItems = this.normalizeThirdPartyBindings(res.data).filter(item => !boundServerIds.has(item.id));
            this.bindableThirdPartyServers = reset
              ? normalizedItems
              : [
                  ...this.bindableThirdPartyServers,
                  ...normalizedItems.filter(item => !this.bindableThirdPartyServers.some(existing => existing.id === item.id))
                ];
            this.bindableThirdPartyHasNextPage = Boolean(
              res.data?.hasNextPage ?? normalizedItems.length >= this.bindableThirdPartyPageSize
            );
            this.bindableThirdPartyPageIndex = pageIndex + 1;
            return;
          }
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('servicenumber.channel.third-party.bindable-load-failed'));
        },
        error: () => {
          this.message.error(this.i18n.fanyi('servicenumber.channel.third-party.bindable-load-failed'));
        }
      });
  }

  onBindableListScroll(event: Event): void {
    if (this.thirdPartyBindingLoading || !this.bindableThirdPartyHasNextPage) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 80) {
      this.loadBindableThirdPartyServers(false);
    }
  }

  bindThirdPartyServer(): void {
    if (!this.selectedThirdPartyServerId) {
      this.message.warning(this.i18n.fanyi('servicenumber.channel.third-party.select-required'));
      return;
    }
    if (!this.selectedThirdPartyEventTypes.length) {
      this.message.warning(this.i18n.fanyi('tenant.third-party-service.events.required'));
      return;
    }

    const requestPath = this.editingThirdPartyBinding
      ? ConstantUtil.ADMIN_SERVICENUMBER_EXTENSION_V1_UPDATE
      : ConstantUtil.ADMIN_SERVICENUMBER_EXTENSION_V1_BIND;

    this.thirdPartyBindingSubmitting = true;
    this.http
      .post(requestPath, {
        serviceNumberId: this.serviceNumberId,
        extensionServerId: this.selectedThirdPartyServerId,
        eventTypes: this.selectedThirdPartyEventTypes
      })
      .pipe(
        finalize(() => {
          this.thirdPartyBindingSubmitting = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res.success) {
            this.message.success(
              this.i18n.fanyi(
                this.editingThirdPartyBinding
                  ? 'servicenumber.channel.third-party.update-success'
                  : 'servicenumber.channel.third-party.bind-success'
              )
            );
            this.thirdPartyBindingModalVisible = false;
            this.closeThirdPartyBindingModal();
            this.closeThirdPartyEventModal();
            this.loadThirdPartyBindings();
            return;
          }
          showFallbackHttpError(
            this.message,
            res.msg,
            this.i18n.fanyi(
              this.editingThirdPartyBinding
                ? 'servicenumber.channel.third-party.update-failed'
                : 'servicenumber.channel.third-party.bind-failed'
            )
          );
        },
        error: () => {
          this.message.error(
            this.i18n.fanyi(
              this.editingThirdPartyBinding
                ? 'servicenumber.channel.third-party.update-failed'
                : 'servicenumber.channel.third-party.bind-failed'
            )
          );
        }
      });
  }

  unbindThirdPartyServer(item: ThirdPartyServerBindingItem): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('servicenumber.channel.third-party.unbind-title'),
      nzContent: this.i18n.fanyi('servicenumber.channel.third-party.unbind-content'),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () =>
        new Promise<void>(resolve => {
          this.http
            .post(ConstantUtil.ADMIN_SERVICENUMBER_EXTENSION_V1_UNBIND, {
              serviceNumberId: this.serviceNumberId,
              extensionServerId: item.id
            })
            .subscribe({
              next: res => {
                if (res.success) {
                  this.message.success(this.i18n.fanyi('servicenumber.channel.third-party.unbind-success'));
                  this.loadThirdPartyBindings();
                  resolve();
                  return;
                }
                showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('servicenumber.channel.third-party.unbind-failed'));
                resolve();
              },
              error: () => {
                this.message.error(this.i18n.fanyi('servicenumber.channel.third-party.unbind-failed'));
                resolve();
              }
            });
        })
    });
  }

  getThirdPartyEventTypeLabel(type: string): string {
    const normalized = this.normalizeThirdPartyEventType(type);
    return normalized ? this.i18n.fanyi(this.thirdPartyEventTypeLabels[normalized]) : type;
  }

  isThirdPartyEventTypeSelected(eventType: ThirdPartyEventType): boolean {
    return this.selectedThirdPartyEventTypes.includes(eventType);
  }

  toggleThirdPartyEventType(eventType: ThirdPartyEventType, checked: boolean): void {
    if (checked) {
      if (!this.selectedThirdPartyEventTypes.includes(eventType)) {
        this.selectedThirdPartyEventTypes = [...this.selectedThirdPartyEventTypes, eventType];
      }
      return;
    }
    this.selectedThirdPartyEventTypes = this.selectedThirdPartyEventTypes.filter(item => item !== eventType);
  }

  selectThirdPartyServer(item: ThirdPartyServerBindingItem): void {
    this.selectedThirdPartyServerId = item.id;
    this.selectedThirdPartyEventTypes = item.eventTypes.length ? [...item.eventTypes] : [...this.thirdPartyEventTypeOptions];
    this.thirdPartyBindingModalVisible = false;
    this.thirdPartyEventModalVisible = true;
  }

  isThirdPartyServerSelected(serverId: string): boolean {
    return this.selectedThirdPartyServerId === serverId;
  }

  shouldShowThirdPartyEventEditor(): boolean {
    return !!this.selectedThirdPartyServerId;
  }

  getSelectedThirdPartyServer(): ThirdPartyServerBindingItem | null {
    if (this.editingThirdPartyBinding) {
      return this.editingThirdPartyBinding;
    }
    return this.bindableThirdPartyServers.find(item => item.id === this.selectedThirdPartyServerId) ?? null;
  }

  getThirdPartyBindingModalTitleKey(): string {
    return this.editingThirdPartyBinding
      ? 'servicenumber.channel.third-party.edit-modal-title'
      : 'servicenumber.channel.third-party.bind-modal-title';
  }

  isThirdPartyBindingEditing(): boolean {
    return !!this.editingThirdPartyBinding;
  }

  private normalizeThirdPartyBindings(data: any): ThirdPartyServerBindingItem[] {
    const items = Array.isArray(data) ? data : (data?.items ?? data?.list ?? data?.records ?? []);
    return items.map((item: any, index: number) => {
      const rawEventTypes = Array.isArray(item?.eventTypes)
        ? item.eventTypes
        : Array.isArray(item?.events)
          ? item.events
          : typeof item?.eventTypes === 'string'
            ? item.eventTypes
                .split(',')
                .map((part: string) => part.trim())
                .filter(Boolean)
            : [];
      return {
        id: item?.extensionServerId ?? item?.id ?? item?.thirdPartyServerId ?? item?.serverId ?? `binding-${index}`,
        bindingId: item?.relationId ?? item?.bindingId ?? item?.id ?? `binding-${index}`,
        name: item?.name ?? item?.serverName ?? '-',
        webhookUrl: item?.url ?? item?.webhookUrl ?? '',
        eventTypes: rawEventTypes
          .map((type: string) => this.normalizeThirdPartyEventType(type))
          .filter((type: ThirdPartyEventType | null): type is ThirdPartyEventType => !!type)
      };
    });
  }

  private buildServiceNumberExtensionListPath(serviceNumberId: string): string {
    return ConstantUtil.ADMIN_SERVICENUMBER_EXTENSION_V1_LIST.replace('{serviceNumberId}', encodeURIComponent(serviceNumberId));
  }

  private normalizeThirdPartyEventType(type: string | null | undefined): ThirdPartyEventType | null {
    const normalized = String(type ?? '')
      .trim()
      .toLowerCase();
    switch (normalized) {
      case 'follow':
        return 'Follow';
      case 'unfollow':
        return 'Unfollow';
      case 'message':
      case 'messages':
      case 'messaging':
        return 'Messaging';
      case 'postback':
        return 'Postback';
      default:
        return null;
    }
  }
}
